import React, {
  useState,
  createContext,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import JourneyListingUserDetailDto from "../../../../types/dtos/dashboards/manager/listings/journey/JourneyListingUserDetailDto";
import JourneyListingDto from "../../../../types/dtos/dashboards/manager/listings/journey/JourneyListingDto";
import JourneyUserListItem from "./JourneyUserListItem";
import Accordion from "../../../common/Accordion";
import { JourneyFormButtonArg } from "../../../../types/dashboard/JourneyFormDropDownItems";

// Create a context to reset pagination across all user items
export const JourneyResetPaginationContext = createContext<number>(0);

interface JourneyUserViewProps {
  users: JourneyListingUserDetailDto[] | null;
  journeys: JourneyListingDto[] | null;
  hasPreventJourneySwitchConfigEnabled: boolean;
  onUpdateClick: (journey: JourneyListingDto) => void;
  onViewClick: (journey: JourneyListingDto) => void;
  onDualPrepItemClick: (
    user: JourneyListingUserDetailDto,
    journey: JourneyListingDto,
    arg: JourneyFormButtonArg
  ) => void;
  expandAll?: boolean;
  onExpandedSectionsChange?: (sections: string[]) => void;
  expandedSections?: string[];
}

const JourneyUserView: React.FC<JourneyUserViewProps> = ({
  users,
  journeys,
  hasPreventJourneySwitchConfigEnabled,
  onUpdateClick,
  onViewClick,
  onDualPrepItemClick,
  expandAll,
  onExpandedSectionsChange,
  expandedSections,
}) => {
  const [activeAccordionSections, setActiveAccordionSections] = useState<
    string[]
  >(expandedSections || []);

  // Counter to trigger pagination resets
  const [resetPaginationCounter, setResetPaginationCounter] = useState(0);

  // Reset pagination whenever journeys change (which happens when filters are applied)
  useEffect(() => {
    setResetPaginationCounter((prev) => prev + 1);
  }, [journeys]);

  // Get all accordion item keys for the users (memoized to prevent unnecessary recalculations)
  const allUserKeys = useMemo(() => {
    const keys =
      users?.map((user, index) => `user-${user.employeeId}-${index}`) || [];
    // Check for duplicates
    const duplicates = keys.filter((key, index) => keys.indexOf(key) !== index);
    if (duplicates.length > 0) {
      console.warn("Duplicate user keys found:", duplicates);
    }
    return keys;
  }, [users]);

  // Handle section changes without causing excessive updates
  const handleSectionsChange = useCallback(
    (sections: string[]) => {
      if (
        JSON.stringify(sections) !== JSON.stringify(activeAccordionSections)
      ) {
        setActiveAccordionSections(sections);

        // Always call the parent callback when sections change
        if (onExpandedSectionsChange) {
          onExpandedSectionsChange(sections);
        }
      }
    },
    [
      activeAccordionSections,
      expandedSections,
      onExpandedSectionsChange,
      allUserKeys,
      expandAll,
    ]
  );

  // Listen for external expandAll changes
  useEffect(() => {
    if (expandAll === true) {
      setActiveAccordionSections(allUserKeys);
    }
  }, [expandAll, allUserKeys]);

  // Listen for external expandedSections changes
  useEffect(() => {
    if (
      expandedSections !== undefined &&
      JSON.stringify(expandedSections) !==
        JSON.stringify(activeAccordionSections)
    ) {
      setActiveAccordionSections(expandedSections);
    }
  }, [expandedSections]);

  if (!users || !journeys) {
    return (
      <div className="p-4 text-center text-gray-500">No data available.</div>
    );
  }

  return (
    <JourneyResetPaginationContext.Provider value={resetPaginationCounter}>
      <div className="user-view mt-4">
        {users.length === 0 ? (
          <div className="p-4 text-center text-gray-500">No users found.</div>
        ) : (
          <>
            <Accordion
              allowMultipleActiveSections={true}
              activeSection={activeAccordionSections}
              onActiveSectionChange={handleSectionsChange}
              allItemKeys={allUserKeys}
            >
              {users.map((user, index) => (
                <JourneyUserListItem
                  key={`user-${user.employeeId}-${index}`}
                  user={user}
                  userIndex={index}
                  journeys={journeys}
                  hasPreventJourneySwitchConfigEnabled={
                    hasPreventJourneySwitchConfigEnabled
                  }
                  onUpdateClick={onUpdateClick}
                  onViewClick={onViewClick}
                  onDualPrepItemClick={onDualPrepItemClick}
                />
              ))}
            </Accordion>
          </>
        )}
      </div>
    </JourneyResetPaginationContext.Provider>
  );
};

export default JourneyUserView;
